import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import AdSlot from "../../components/AdSlot/AdSlot";
import NewsGrid from "../../components/NewsGrid/NewsGrid";
import { fetchNewsById, fetchNewsByCategory } from "../../api/newsApi";
import { shareContent } from "../../utils/share";
import "./NewsDetailsPage.css";
import SEO from "../../components/SEO/SEO";

function NewsDetailsPage() {
  const { categorySlug, id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareMessage, setShareMessage] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadNews() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchNewsById(categorySlug, id);
        if (isMounted) setNews(data);
      } catch (err) {
        if (isMounted) setError("We couldn't load this article. Please try again.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadNews();
    window.scrollTo(0, 0);
    return () => {
      isMounted = false;
    };
  }, [categorySlug, id]);

  useEffect(() => {
    let isMounted = true;

    async function loadRelated() {
      setRelatedLoading(true);
      try {
        const data = await fetchNewsByCategory(categorySlug, { page: 1, limit: 9 });
        if (isMounted) {
          const latest = (data.items || []).filter((item) => String(item.id) !== String(id)).slice(0, 6);
          setRelatedNews(latest);
        }
      } catch (err) {
        if (isMounted) setRelatedNews([]);
      } finally {
        if (isMounted) setRelatedLoading(false);
      }
    }

    loadRelated();
    return () => {
      isMounted = false;
    };
  }, [categorySlug, id]);

  if (loading) return <Loader label="Loading article..." />;
  if (error) return <ErrorMessage message={error} />;
  if (!news) return null;

  async function handleShare() {
    const result = await shareContent({
      title: news.title,
      text: news.subtitle || news.title,
      url: window.location.href,
      imageUrl: news.image,
    });
    if (!result.shared) {
      setShareMessage(result.copied ? "Link copied to clipboard" : "Couldn't share this article");
      setTimeout(() => setShareMessage(null), 2500);
    }
  }

  return (
    <main className="tt-details-wrap">
      <SEO
        title={news.title}
        description={news.subtitle || news.title}
        image={news.image}
        url={window.location.href}
      />
      <nav className="tt-details-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        {news.category && (
          <>
            <span className="tt-details-crumb-sep">/</span>
            {(() => {
              const linkSlug =
                news.categoryLinkSlug || (news.categorySlug !== "trending" ? news.categorySlug : null);
              return linkSlug ? (
                <Link to={`/category/${linkSlug}`}>{news.category}</Link>
              ) : (
                <span>{news.category}</span>
              );
            })()}
          </>
        )}
      </nav>

      <h1 className="tt-details-title">{news.title}</h1>
      {news.subtitle && <p className="tt-details-subtitle">{news.subtitle}</p>}

      <div className="tt-details-byline">
        {news.author && <span className="tt-details-author">{news.author}</span>}
        {news.publishedAt && <span className="tt-details-date">Published - {news.publishedAt}</span>}

        <button type="button" className="tt-details-share-btn" onClick={handleShare}>
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <circle cx="18" cy="5" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <circle cx="6" cy="12" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <circle cx="18" cy="19" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <line x1="8.2" y1="10.8" x2="15.8" y2="6.2" stroke="currentColor" strokeWidth="1.8" />
            <line x1="8.2" y1="13.2" x2="15.8" y2="17.8" stroke="currentColor" strokeWidth="1.8" />
          </svg>
          Share
        </button>
        {shareMessage && <span className="tt-details-share-msg" role="status">{shareMessage}</span>}
      </div>

      {news.image && (
        <figure className="tt-details-figure">
          <img src={news.image} alt={news.title} className="tt-details-image" />
          {news.imageCaption && <figcaption className="tt-details-caption">{news.imageCaption}</figcaption>}
        </figure>
      )}

      <AdSlot slot="3333333333" variant="rectangle" label="Advertisement" />

      <div
        className="tt-details-content"
        dangerouslySetInnerHTML={{ __html: news.content || "" }}
      />

      {news.tags && news.tags.length > 0 && (
        <ul className="tt-details-tags" aria-label="Topics">
          {news.tags.map((tag) => (
            <li key={tag} className="tt-details-tag">
              {tag}
            </li>
          ))}
        </ul>
      )}

      <AdSlot slot="4444444444" variant="leaderboard" label="Advertisement" />

      {!relatedLoading && relatedNews.length > 0 && (
        <section className="tt-details-related">
          <h2 className="tt-details-related-title">
            Latest in {news.category || "this category"}
          </h2>
          <NewsGrid newsList={relatedNews} showHero={false} showAds={false} />
        </section>
      )}
    </main>
  );
}

export default NewsDetailsPage;
